import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Boxes } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NftProjectCreate = ({ user }) => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    contract_draft_id: '',
    name: '',
    standard: '',
    base_name: '',
    description: '',
    quantity: 10
  });

  useEffect(() => { fetchDrafts(); }, []);

  const fetchDrafts = async () => {
    try {
      const res = await axios.get(`${API}/contracts/drafts`);
      const readyDrafts = (res.data.drafts || []).filter(d => d.status === 'READY');
      setDrafts(readyDrafts);
    } catch (err) {
      console.error('Error fetching drafts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDraftSelect = (draftId) => {
    const draft = drafts.find(d => d.id === draftId);
    if (draft) {
      setForm(prev => ({
        ...prev,
        contract_draft_id: draftId,
        standard: draft.standard || 'ERC721',
        name: prev.name || (draft.params?.contract_name ? `${draft.params.contract_name} Metadata` : ''),
        base_name: prev.base_name || draft.params?.nft_name || draft.params?.contract_name || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await axios.post(`${API}/nft/projects`, {
        ...form,
        quantity: parseInt(form.quantity, 10)
      });
      if (res.data.success) {
        navigate(`/creator/nft/projects/${res.data.project.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading...</div>;
  }

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: theme.radius.sm,
    border: `1px solid ${theme.colors.borders}`, fontSize: '14px',
    color: theme.colors.text.primary, background: theme.colors.bg.card,
    outline: 'none', boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: 600,
    color: theme.colors.text.secondary, marginBottom: '6px'
  };

  return (
    <div data-testid="nft-project-create-page" style={{ padding: '40px', maxWidth: '640px' }}>
      <button
        data-testid="back-to-projects-btn"
        onClick={() => navigate('/creator/nft/projects')}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: theme.colors.text.secondary, fontSize: '14px', marginBottom: '24px', padding: 0
        }}
      >
        <ArrowLeft size={16} /> Back to Projects
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '14px',
          background: `${theme.colors.primary}10`, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Boxes size={24} style={{ color: theme.colors.primary }} />
        </div>
        <div>
          <h1 style={{ fontFamily: theme.fonts.heading, fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary }}>
            New NFT Metadata Project
          </h1>
          <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>
            Link to a deployed contract and configure your metadata
          </p>
        </div>
      </div>

      {drafts.length === 0 ? (
        <div style={{
          background: theme.colors.bg.secondary, borderRadius: theme.radius.card,
          padding: '40px', textAlign: 'center', border: `1px solid ${theme.colors.borders}`
        }}>
          <p style={{ color: theme.colors.text.secondary, fontSize: '14px', marginBottom: '16px' }}>
            No deployed contracts found. Deploy a contract first (mark as READY with a contract address).
          </p>
          <button
            onClick={() => navigate('/creator/contracts/templates')}
            style={{
              padding: '10px 20px', borderRadius: theme.radius.cta,
              background: theme.colors.primary, color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 600
            }}
          >
            Go to Contract Templates
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Contract Draft Select */}
            <div>
              <label style={labelStyle}>Deployed Contract *</label>
              <select
                data-testid="draft-select"
                value={form.contract_draft_id}
                onChange={(e) => handleDraftSelect(e.target.value)}
                required
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">Select a deployed contract...</option>
                {drafts.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.params?.contract_name || d.template_name} — {d.standard} ({d.chain || 'No chain'})
                  </option>
                ))}
              </select>
            </div>

            {/* Project Name */}
            <div>
              <label style={labelStyle}>Project Name *</label>
              <input
                data-testid="project-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. CoolApes Collection"
                required
                style={inputStyle}
              />
            </div>

            {/* Standard (auto-filled from draft) */}
            <div>
              <label style={labelStyle}>Standard</label>
              <input
                data-testid="standard-input"
                type="text"
                value={form.standard}
                readOnly
                style={{ ...inputStyle, background: theme.colors.bg.tertiary, color: theme.colors.text.muted }}
              />
            </div>

            {/* Base Name */}
            <div>
              <label style={labelStyle}>Base Name (for token naming pattern) *</label>
              <input
                data-testid="base-name-input"
                type="text"
                value={form.base_name}
                onChange={(e) => setForm(prev => ({ ...prev, base_name: e.target.value }))}
                placeholder='e.g. CoolApe — produces "CoolApe #1", "CoolApe #2"...'
                required
                style={inputStyle}
              />
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description</label>
              <textarea
                data-testid="description-input"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Description that appears on each token metadata"
                rows={3}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* Quantity */}
            <div>
              <label style={labelStyle}>Quantity (1–10,000) *</label>
              <input
                data-testid="quantity-input"
                type="number"
                value={form.quantity}
                onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value }))}
                min={1}
                max={10000}
                required
                style={inputStyle}
              />
            </div>

            {error && (
              <div data-testid="create-project-error" style={{
                padding: '12px 16px', borderRadius: theme.radius.sm,
                background: `${theme.colors.status.error}10`, color: theme.colors.status.error,
                fontSize: '13px', fontWeight: 500
              }}>
                {error}
              </div>
            )}

            <button
              data-testid="create-project-submit-btn"
              type="submit"
              disabled={saving || !form.contract_draft_id}
              style={{
                padding: '12px 28px', borderRadius: theme.radius.cta,
                background: saving ? theme.colors.text.muted : theme.colors.primary,
                color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '15px', fontWeight: 600, width: 'fit-content'
              }}
            >
              {saving ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default NftProjectCreate;
